(() => {
  const BASE = "https://cdn.shopify.com/s/files/1/0959/0531/7129/files/";
  const TREE_URL = BASE + "vehicle-tree-v2.json";
  const STORAGE_KEY = "ridesrus_vehicle_v1";

  async function fetchJson(url) {
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`Fetch failed (${r.status}) ${url}`);
    return r.json();
  }

  function saveVehicle(v) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
    window.dispatchEvent(new CustomEvent("vehicle:changed", { detail: v }));
  }

  function loadVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function clearVehicle() {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("vehicle:changed", { detail: null }));
  }

  function formatVehicle(v) {
    if (!v) return "";
    const p = [];
    if (v.year && v.year !== "0") p.push(v.year);
    if (v.make) p.push(v.make);
    if (v.model) p.push(v.model);
    if (v.cc && v.cc !== "_any") p.push(`${v.cc}cc`);
    return p.join(" ");
  }

  function getMakes(tree) {
    return Object.keys((tree && tree.makes) || {}).sort();
  }

  function getYearsForMake(tree, make) {
  const models = tree?.makes?.[make]?.models || {};
  const years = new Set();

  Object.values(models).forEach(modelNode => {
    const ccObj = modelNode?.cc || {};

    Object.values(ccObj).forEach(ccNode => {
      const yearList = ccNode?.years || [];

      yearList.forEach(year => {
        if (Number(year) !== 0) {
          years.add(String(year));
        }
      });
    });
  });

  return Array.from(years).sort((a, b) => Number(a) - Number(b));
}

  function modelHasYear(modelNode, year) {
  const ccObj = modelNode?.cc || {};

  return Object.values(ccObj).some(ccNode => {
    const years = (ccNode?.years || []).map(String);
    return years.includes(String(year)) || years.includes("0");
  });
}

  function getModelsForMakeYear(tree, make, year) {
    const models = tree?.makes?.[make]?.models || {};
    return Object.keys(models)
      .filter(modelName => modelHasYear(models[modelName], year))
      .sort();
  }

  function ccHasYear(ccNode, year) {
  const years = (ccNode?.years || []).map(String);
  return years.includes(String(year)) || years.includes("0");
  }

  function getCcsForMakeModelYear(tree, make, model, year) {
    const ccObj = tree?.makes?.[make]?.models?.[model]?.cc || {};
    const keys = Object.keys(ccObj);

    const real = keys
      .filter(k => k !== "_any")
      .filter(k => ccHasYear(ccObj[k], year))
      .sort((a, b) => Number(a) - Number(b));

    const any = keys.includes("_any") && ccHasYear(ccObj["_any"], year) ? ["_any"] : [];

    return [...real, ...any];
  }

  function createCombo({ placeholder, disabled }) {
    const wrap = document.createElement("div");
    wrap.className = "fitment-combo";

    const input = document.createElement("input");
    input.className = "fitment-input";
    input.type = "text";
    input.placeholder = placeholder;
    input.autocomplete = "off";
    input.spellcheck = false;
    input.disabled = !!disabled;

    const list = document.createElement("div");
    list.className = "fitment-list";
    list.setAttribute("role", "listbox");

    wrap.appendChild(input);
    wrap.appendChild(list);

    return { wrap, input, list };
  }

  function mountCombo(combo, itemsProvider, onPick) {
    let items = [];
    let filtered = [];
    let activeIndex = -1;

    function open() {
      combo.list.style.display = "block";
    }

    function close() {
      combo.list.style.display = "none";
      activeIndex = -1;
      updateActive();
    }

    function render() {
      combo.list.innerHTML = "";

      filtered.forEach((txt, idx) => {
        const opt = document.createElement("div");
        opt.className = "fitment-option";
        opt.textContent = txt;
        opt.setAttribute("role", "option");
        opt.setAttribute("data-index", String(idx));
        opt.addEventListener("mousedown", e => {
          e.preventDefault();
          pick(idx);
        });
        combo.list.appendChild(opt);
      });

      if (filtered.length) open();
      else close();
    }

    function updateActive() {
      const options = combo.list.querySelectorAll(".fitment-option");
      options.forEach((el, i) => {
        el.setAttribute("aria-selected", i === activeIndex ? "true" : "false");
        if (i === activeIndex) el.scrollIntoView({ block: "nearest" });
      });
    }

    function pick(idx) {
      const val = filtered[idx];
      if (!val) return;
      combo.input.value = val;
      close();
      onPick(val);
    }

    function refresh() {
      items = itemsProvider() || [];
      const q = combo.input.value.trim().toLowerCase();

      filtered = q
        ? items.filter(x => x.toLowerCase().includes(q)).slice(0, 200)
        : items.slice(0, 200);

      render();
    }

    combo.input.addEventListener("focus", () => {
      refresh();
      if (filtered.length) open();
    });

    combo.input.addEventListener("input", refresh);

    combo.input.addEventListener("keydown", e => {
      if (combo.list.style.display !== "block" && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        refresh();
        if (filtered.length) open();
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        activeIndex = Math.min(filtered.length - 1, activeIndex + 1);
        updateActive();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        activeIndex = Math.max(0, activeIndex - 1);
        updateActive();
      } else if (e.key === "Enter") {
        if (activeIndex >= 0) {
          e.preventDefault();
          pick(activeIndex);
        } else {
          const exact = items.find(x => x.toLowerCase() === combo.input.value.trim().toLowerCase());
          if (exact) {
            e.preventDefault();
            combo.input.value = exact;
            close();
            onPick(exact);
          }
        }
      } else if (e.key === "Escape") {
        close();
      }
    });

    combo.input.addEventListener("blur", () => {
      setTimeout(() => close(), 120);
    });

    return {
      setDisabled: d => {
        combo.input.disabled = !!d;
        if (d) close();
      },
      setValue: v => {
        combo.input.value = v || "";
      },
      getValue: () => combo.input.value.trim(),
      refresh
    };
  }

  async function mountSelector(container) {
    container.innerHTML = "";

    const card = document.createElement("div");
    card.className = "fitment-wrap";
    card.innerHTML = `
      <div class="fitment-row">
        <div class="fitment-title">
          <strong>Find parts for your bike</strong>
          <div class="fitment-sub">Select your vehicle to see compatible products</div>
        </div>

        <div class="fitment-controls" data-role="controls"></div>

        <div class="fitment-current" data-role="current" hidden>
          <span class="fitment-current-label">Your bike:</span>
          <span class="fitment-current-value"></span>
          <button class="fitment-link" type="button" data-role="change">Change</button>
          <span class="fitment-sep">·</span>
          <button class="fitment-link" type="button" data-role="clear">Clear</button>
        </div>
      </div>
    `;
    container.appendChild(card);

    const controls = card.querySelector('[data-role="controls"]');
    const current = card.querySelector('[data-role="current"]');
    const currentValue = card.querySelector(".fitment-current-value");
    const btnChange = card.querySelector('[data-role="change"]');
    const btnClear = card.querySelector('[data-role="clear"]');

    const makeCombo = createCombo({ placeholder: "Type to find make…", disabled: false });
    const modelCombo = createCombo({ placeholder: "Type to find model…", disabled: true });

    const yearSelect = document.createElement("select");
    yearSelect.className = "fitment-select";
    yearSelect.disabled = true;

    const ccSelect = document.createElement("select");
    ccSelect.className = "fitment-select";
    ccSelect.disabled = true;

    const goBtn = document.createElement("button");
    goBtn.className = "fitment-btn";
    goBtn.type = "button";
    goBtn.textContent = "Search";
    goBtn.disabled = true;

    controls.appendChild(makeCombo.wrap);
    controls.appendChild(yearSelect);
    controls.appendChild(modelCombo.wrap);
    controls.appendChild(ccSelect);
    controls.appendChild(goBtn);

    let tree;
    try {
      tree = await fetchJson(TREE_URL);
    } catch (e) {
      console.error(e);
      card.innerHTML = `<p>Fitment selector unavailable.</p>`;
      return;
    }

    function setSelectOptions(sel, placeholder, values, formatFn) {
      sel.innerHTML = "";

      const opt0 = document.createElement("option");
      opt0.value = "";
      opt0.textContent = placeholder;
      sel.appendChild(opt0);

      (values || []).forEach(v => {
        const opt = document.createElement("option");
        opt.value = v;
        opt.textContent = formatFn ? formatFn(v) : v;
        sel.appendChild(opt);
      });
    }

    function updateGoEnabled() {
      const make = makeCtl.getValue();
      const year = yearSelect.value;
      const model = modelCtl.getValue();
      const cc = ccSelect.value;

      goBtn.disabled = !(make && year && model && cc);
    }

    function resetAfterMake() {
      yearSelect.value = "";
      yearSelect.disabled = true;
      setSelectOptions(yearSelect, "Year", []);

      modelCtl.setValue("");
      modelCtl.setDisabled(true);

      ccSelect.value = "";
      ccSelect.disabled = true;
      ccSelect.hidden = false;
      setSelectOptions(ccSelect, "CC", []);

      goBtn.disabled = true;
    }

    function resetAfterYear() {
      modelCtl.setValue("");
      modelCtl.setDisabled(true);

      ccSelect.value = "";
      ccSelect.disabled = true;
      ccSelect.hidden = false;
      setSelectOptions(ccSelect, "CC", []);

      goBtn.disabled = true;
    }

    function resetAfterModel() {
      ccSelect.value = "";
      ccSelect.disabled = true;
      ccSelect.hidden = false;
      setSelectOptions(ccSelect, "CC", []);
      goBtn.disabled = true;
    }

    function applyCcOptions() {
      const make = makeCtl.getValue();
      const year = yearSelect.value;
      const model = modelCtl.getValue();

      if (!make || !year || !model) {
        resetAfterModel();
        return;
      }

      const ccs = getCcsForMakeModelYear(tree, make, model, year);

      if (ccs.length === 0) {
        setSelectOptions(ccSelect, "CC", ["_any"], v => v === "_any" ? "Any / Not sure" : v);
        ccSelect.value = "_any";
        ccSelect.disabled = true;
        ccSelect.hidden = true;
      } else if (ccs.length === 1) {
        setSelectOptions(ccSelect, "CC", ccs, v => v === "_any" ? "Any / Not sure" : v);
        ccSelect.value = ccs[0];
        ccSelect.disabled = true;
        ccSelect.hidden = true;
      } else {
        setSelectOptions(ccSelect, "Select CC", ccs, v => v === "_any" ? "Any / Not sure" : v);
        ccSelect.value = "";
        ccSelect.disabled = false;
        ccSelect.hidden = false;
      }

      updateGoEnabled();
    }

    const makeCtl = mountCombo(
      makeCombo,
      () => getMakes(tree),
      pickedMake => {
        resetAfterMake();

        const years = getYearsForMake(tree, pickedMake);
        yearSelect.disabled = years.length === 0;
        setSelectOptions(yearSelect, "Year", years);

        updateGoEnabled();
      }
    );

    const modelCtl = mountCombo(
      modelCombo,
      () => {
        const make = makeCtl.getValue();
        const year = yearSelect.value;
        if (!make || !year) return [];
        return getModelsForMakeYear(tree, make, year);
      },
      () => {
        applyCcOptions();
      }
    );

    makeCombo.input.addEventListener("input", () => {
      resetAfterMake();

      const make = makeCtl.getValue();
      if (make && tree?.makes?.[make]) {
        const years = getYearsForMake(tree, make);
        yearSelect.disabled = years.length === 0;
        setSelectOptions(yearSelect, "Year", years);
      }

      updateGoEnabled();
    });

    yearSelect.addEventListener("change", () => {
      resetAfterYear();

      const make = makeCtl.getValue();
      const year = yearSelect.value;

      if (make && year) {
        modelCtl.setDisabled(false);
        modelCtl.refresh();
      }

      updateGoEnabled();
    });

    modelCombo.input.addEventListener("input", () => {
      resetAfterModel();
      updateGoEnabled();
    });

    ccSelect.addEventListener("change", updateGoEnabled);

    function updateCurrentUI() {
      const v = loadVehicle();
      if (v && v.make && v.model) {
        current.hidden = false;
        currentValue.textContent = formatVehicle(v);
      } else {
        current.hidden = true;
        currentValue.textContent = "";
      }
    }

    const saved = loadVehicle();
    if (saved?.make && tree?.makes?.[saved.make]) {
      makeCtl.setValue(saved.make);

      const years = getYearsForMake(tree, saved.make);
      yearSelect.disabled = years.length === 0;
      setSelectOptions(yearSelect, "Year", years);

      if (saved.year && saved.year !== "0" && years.includes(String(saved.year))) {
        yearSelect.value = String(saved.year);

        modelCtl.setDisabled(false);

        const models = getModelsForMakeYear(tree, saved.make, saved.year);
        if (saved.model && models.includes(saved.model)) {
          modelCtl.setValue(saved.model);
          applyCcOptions();

          if (saved.cc) {
            const ccs = getCcsForMakeModelYear(tree, saved.make, saved.model, saved.year);
            if (ccs.includes(saved.cc)) ccSelect.value = saved.cc;
          }
        }
      }

      updateGoEnabled();
    }

    updateCurrentUI();

    goBtn.addEventListener("click", () => {
      const make = makeCtl.getValue();
      const year = yearSelect.value;
      const model = modelCtl.getValue();
      const cc = ccSelect.value || "_any";

      if (!make || !tree?.makes?.[make]) return alert("Please select a valid Make from the list.");
      if (!year) return alert("Please select a Year.");
      if (!model || !getModelsForMakeYear(tree, make, year).includes(model)) return alert("Please select a valid Model from the list.");

      const vehicle = {
        make,
        model,
        cc,
        year
      };

      saveVehicle(vehicle);

      const params = new URLSearchParams(vehicle);
      window.location.href = `/pages/fitment-results?${params.toString()}`;
    });

    btnClear.addEventListener("click", () => {
      clearVehicle();
      makeCtl.setValue("");
      resetAfterMake();
      updateGoEnabled();
      updateCurrentUI();
    });

    btnChange.addEventListener("click", () => {
      container.scrollIntoView({ behavior: "smooth", block: "start" });
      makeCombo.input.focus();
    });

    window.addEventListener("vehicle:changed", () => updateCurrentUI());
  }

  document.addEventListener("DOMContentLoaded", () => {
    document
      .querySelectorAll("[data-fitment-selector] .fitment-inner")
      .forEach(node => mountSelector(node));
  });

  window.Fitment = {
    getVehicle: loadVehicle,
    clearVehicle
  };
})();